<script setup>
import { ref, onMounted, computed, watch, onUnmounted } from 'vue'
import {useRouter, useRoute} from 'vue-router'
import {useStore} from 'vuex'
import { API, HOST } from '@/utils/http'
import { centroid } from '@turf/turf'
import { debounce } from 'lodash'
import {formatTimeFromNowNumeric} from '@/utils/datetime'

import { MapSource, MapLayer, MapImageMarker } from '@/components/map'
import MapFeaturePopup from '@/components/map/MapFeaturePopup.vue'
import { toast } from '@/components/ui/toast'

const CACHE_MAX_SIZE = 1000  // Maximum number of entries in the age cache
const CACHE_EXPIRATION_MS = 3600000  // Cache expiration time in milliseconds (1 hour)
const CACHE_BATCH_DELETE_SIZE = 100  // Number of entries to delete at once during eviction

const props = defineProps({
  map: {
    type: Object,
    required: true,
  },
  project: {
    type: Object,
    required: true,
  },
  activeTab: {
    type: String,
    required: true,
  },
})

const router = useRouter()
const route = useRoute()
const store = useStore()

const isLoggedIn = computed(() => store.getters.getIsLoggedIn)
const settings = computed(() => store.getters['user/getUserSettings'])
const beacons = computed(() => store.getters['geo/getBeacons'])
const params = computed(() => store.getters['search/getParams'])
const mapParamsKeys = ['zoom', 'center_lat', 'center_lng', 'south', 'north', 'east', 'west']

const features = computed(() => store.getters['geo/getVisibleFeatures'])

const ageCache = ref(new Map())
const featuresWithAge = computed(() => {
  if (!features.value || !features.value.features) {
    return { type: 'FeatureCollection', features: [] }
  }

  const now = Date.now()
  const featuresWithAgeCalculated = features.value.features.map(feature => {
    const code = feature.properties?.code
    const createdAt = feature.properties?.created_at
    const cacheKey = `${code}-${createdAt}`

    // Check cache first
    if (ageCache.value.has(cacheKey)) {
      const cached = ageCache.value.get(cacheKey)
      // Recalculate if cache is older than expiration time
      if (now - cached.timestamp < CACHE_EXPIRATION_MS) {
        return {
          ...feature,
          properties: {
            ...feature.properties,
            ageInDays: cached.ageInDays
          }
        }
      }
    }

    let ageInDays = 30 // default age for features without created_at
    if (createdAt) {
      const createdAtTime = new Date(createdAt).getTime()
      ageInDays = Math.max(0, (now - createdAtTime) / (1000 * 60 * 60 * 24))
    }

    if (code) {
      ageCache.value.set(cacheKey, {
        ageInDays,
        timestamp: now
      })

      if (ageCache.value.size > CACHE_MAX_SIZE) {
        const keysToDelete = []
        const iterator = ageCache.value.keys()

        while (keysToDelete.length < CACHE_BATCH_DELETE_SIZE) {
          const key = iterator.next().value
          if (key) keysToDelete.push(key)
          else break
        }

        keysToDelete.forEach(key => ageCache.value.delete(key))
      }
    }

    return {
      ...feature,
      properties: {
        ...feature.properties,
        ageInDays: ageInDays
      }
    }
  })

  return {
    type: 'FeatureCollection',
    features: featuresWithAgeCalculated
  }
})

const filterParams = computed(() => {
  return params.value.filter((param) => !mapParamsKeys.includes(param.key))
})

const routeProjectId = computed(() => parseInt(route.params.project_id))

// Popup state
const popupFeature = ref(null)
const popupCoordinates = ref(null)
const showPopup = ref(false)

const isCurrentProject = computed(() => {
  const routeProjectId = Number(route.params.project_id)
  return !isNaN(routeProjectId) && props.project.project_id === routeProjectId
})
const currentPageFeatureCode = computed(() => route.params.featureCode)
const featureCode = computed(() => props.project.paired_feature_code)
const feature = ref(null)
const featureCenter = computed(() => {
  if (!feature.value) return null
  return centroid(feature.value)
})

const showProjectBoundaries = computed(() => store.getters['geo/getProjectBoundariesVisible'])

const getProjectBoundary = async (featureCode) => {
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/geo/feature/${featureCode}`
    })

    return response?.data || null
  } catch (e) {
    toast({
      title: "Project boundary not found",
    })
  }
}

const handleClick = async (feature, e) => {
  if (!feature || !feature.properties) return

  if (feature.properties.type === 'project') {
    router.push({ name: 'Project', params: { org_id: props.project.organization_id, project_id: props.project.project_id } })
  } else if (feature.properties.cluster || feature.properties.point_count) {
    // For clusters, zoom in
    const clusterId = feature.properties.cluster_id
    const source = props.map.getSource(`project-${props.project.project_id}-features-points`)
    if (source && source.getClusterExpansionZoom) {
      source.getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return
        props.map.easeTo({
          center: feature.geometry.coordinates,
          zoom: zoom
        })
      })
    }
  } else {
    if (popupFeature.value && popupFeature.value.properties.code === feature.properties.code) {
      closePopup()
    } else {
      if (showPopup.value) {
        closePopup()
        // Small delay to ensure previous popup is closed
        setTimeout(() => {
          popupFeature.value = feature
          popupCoordinates.value = e && e.lngLat ? e.lngLat.toArray() : feature.geometry.coordinates
          showPopup.value = true
        }, 50)
      } else {
        popupFeature.value = feature
        popupCoordinates.value = e && e.lngLat ? e.lngLat.toArray() : feature.geometry.coordinates
        showPopup.value = true
      }
    }
  }
}

const closePopup = () => {
  showPopup.value = false
  popupFeature.value = null
  popupCoordinates.value = null
}

const loadInitialBeacons = async () => {
  if (!routeProjectId.value) return
  await store.dispatch('geo/fetchBeacons', {
    project_id: routeProjectId.value,
  })
}

const loadData = async () => {
  if (!routeProjectId.value || !isLoggedIn.value || !isCurrentProject.value) return

  store.commit('SET_IS_LOADING_DATA', true)

  const paramsMap = params.value.reduce((acc, param) => {
    acc[param.key] = param.value
    return acc
  }, {})

  const filterParamsMap = filterParams.value.reduce((acc, param) => {
    acc[param.key] = param.value
    return acc
  }, {})

  const payload = {
    project_id: routeProjectId.value,
    shared: filterParamsMap['shared'],
    has_resources: filterParamsMap['has_resources'],
    start_date: filterParamsMap['start_date'],
    end_date: filterParamsMap['end_date'],
    is_meta: false,

    // map params
    south: paramsMap['south'],
    north: paramsMap['north'],
    east: paramsMap['east'],
    west: paramsMap['west'],
  }

  await store.dispatch('geo/fetchFeatures', payload)

  store.commit('SET_IS_LOADING_DATA', false)
}

const updateBounds = async () => {
  props.map.setPadding({ top: 0, bottom: 0, left: 0, right: 0 })

  const bounds = props.map.getBounds()
  const center = props.map.getCenter()

  if (!bounds && !center) return

  const options = {
    center_lat: center.lat,
    center_lng: center.lng,
    zoom: props.map.getZoom(),
    south: bounds.getSouth(),
    north: bounds.getNorth(),
    east: bounds.getEast(),
    west: bounds.getWest()
  }

  for (const [key, value] of Object.entries(options)) {
    store.commit('search/ADD_PARAM', {
      key: key,
      value: value
    })
  }

  localStorage.setItem('mapOptions', JSON.stringify({
    center: props.map.getCenter(),
    zoom: props.map.getZoom()
  }))

  await loadData()
}

const debouncedUpdateBounds = debounce(updateBounds, 400)

const setListeners = () => {
  props.map.on('dragend', debouncedUpdateBounds)
  props.map.on('zoomend', debouncedUpdateBounds)
}

const removeListeners = () => {
  props.map.off('dragend', debouncedUpdateBounds)
  props.map.off('zoomend', debouncedUpdateBounds)
}

onMounted(async () => {
  if (featureCode.value) {
    feature.value = await getProjectBoundary(featureCode.value)
  }

  if (!isCurrentProject.value) return
  await loadInitialBeacons()
  await loadData()
  setListeners()
})

onUnmounted(() => {
  removeListeners()
  ageCache.value.clear()
})

watch(
  () => isCurrentProject.value, async (newValue, oldValue) => {
    if (newValue) {
      await loadData()
      setListeners()
    }

    if (!newValue) {
      removeListeners()
    }
  }
)

watch(
  () => filterParams.value,
  async (newValue, oldValue) => {
    if (!oldValue) return

    await loadData()
  },
  { deep: true, immediate: true }
)
</script>

<template>

<div v-if="isCurrentProject">
  <div v-if="beacons.length" :key="beacons.length">
    <MapImageMarker
      v-for="feature in beacons"
      ping
      :label="`${feature.properties.member_display_name} (~${formatTimeFromNowNumeric(feature.properties.rel_ts, settings.tz)})`"
      :url="feature.properties.member_avatar_url"
      :feature="feature"
      :map="map"
      @click="(e) => handleClick(feature, e)"
    ></MapImageMarker>
  </div>

  <!-- Mixed geometry -->
  <!-- mapbox not rendering mixed geometry if clustering is enabled, so need to use two sources -->
  <MapSource
    :map="map"
    type="geojson"
    :id="'project-' + project.project_id + '-features-mixed'"
    :data="{
      type: 'FeatureCollection',
      features: featuresWithAge.features.filter(feature => feature.geometry.type !== 'Point' && feature.properties.code !== currentPageFeatureCode)
    }"
  >
    <MapLayer
      :map="map"
      :id="'project-' + project.project_id + '-report-fill'"
      :source="'project-' + project.project_id + '-features-mixed'"
      type="fill"
      :options="{
        paint: {
          'fill-color': [
            'case',
            ['!=', ['get', 'paired_project_id'], null],
            '#0039A3',  // Project boundary color
            ['get', 'color']  // Regular feature color
          ],
          'fill-opacity': [
            'case',
            ['!=', ['get', 'paired_project_id'], null],
            0.2,  // Project boundary opacity
            [
              'interpolate',
              ['linear'],
              ['get', 'ageInDays'],
              0, 0.3,     // Very recent (today): 30% opacity
              1, 0.25,    // 1 day old: 25% opacity
              7, 0.2,     // 1 week old: 20% opacity
              30, 0.15,   // 1 month old: 15% opacity
              90, 0.1,    // 3 months old: 10% opacity
              365, 0.05   // 1 year old: 5% opacity
            ]
          ]
        },
        filter: ['all', ['!=', '$type', 'Point'], ['!=', '$type', 'LineString']]
      }"
      @click="(e) => handleClick(e.features[0], e)"
    ></MapLayer>
    <MapLayer
      :map="map"
      :id="'project-' + project.project_id + '-report-lines'"
      :source="'project-' + project.project_id + '-features-mixed'"
      type="line"
      :options="{
        paint: {
          'line-color': [
            'case',
            ['!=', ['get', 'paired_project_id'], null],
            '#0039A3',  // Project boundary color
            ['get', 'color']  // Regular feature color
          ],
          'line-width': 2,
          'line-dasharray': [
            'case',
            ['!=', ['get', 'paired_project_id'], null],
            ['literal', [4, 2]],  // Dashed line for project boundary
            ['literal', [1, 0]]   // Solid line for regular features
          ],
          'line-opacity': [
            'case',
            ['!=', ['get', 'paired_project_id'], null],
            1,  // Full opacity for project boundary
            [
              'interpolate',
              ['linear'],
              ['get', 'ageInDays'],
              0, 0.9,     // Very recent (today): 90% opacity
              1, 0.8,     // 1 day old: 80% opacity
              7, 0.7,     // 1 week old: 70% opacity
              30, 0.6,    // 1 month old: 60% opacity
              90, 0.4,    // 3 months old: 40% opacity
              365, 0.2    // 1 year old: 20% opacity
            ]
          ]
        },
        filter: ['!=', '$type', 'Point']
      }"
      @click="(e) => handleClick(e.features[0], e)"
    ></MapLayer>
  </MapSource>

  <!-- Points, Clusters and Images -->
  <MapSource
    :map="map"
    type="geojson"
    :id="'project-' + project.project_id + '-features-points'"
    :data="{
      type: 'FeatureCollection',
      features: featuresWithAge.features.filter(feature => feature.geometry.type === 'Point' && feature.properties.code !== currentPageFeatureCode)
    }"
    :options="{
      cluster: true,
      clusterMaxZoom: 13,
      // clusterRadius: 50,
      clusterMinPoints: 6
    }"
  >
    <MapLayer
      :map="map"
      :id="'project-' + project.project_id + '-clusters'"
      :source="'project-' + project.project_id + '-features-points'"
      type="circle"
      :options="{
        paint: {
          'circle-color': ['step', ['get', 'point_count'], '#2259ff', 100, '#2259ff', 750, '#2259ff'],
          'circle-radius': ['step', ['get', 'point_count'], 20, 100, 30, 750, 40],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#fff'
        },
        filter: ['all', ['has', 'point_count']]
      }"
      @click="(e) => handleClick(e.features[0], e)"
    ></MapLayer>
    <MapLayer
      :map="map"
      :id="'project-' + project.project_id + '-cluster-count'"
      :source="'project-' + project.project_id + '-features-points'"
      type="symbol"
      :options="{
        layout: {
          'text-field': ['get', 'point_count_abbreviated'],
          'text-size': 18
        },
        paint: {
          'text-color': '#fff'
        },
        filter: ['all', ['has', 'point_count']]
      }"
      @click="(e) => handleClick(e.features[0], e)"
    ></MapLayer>

    <MapLayer
      :map="map"
      :id="'project-' + project.project_id + '-unclustered-point'"
      :source="'project-' + project.project_id + '-features-points'"
      type="circle"
      :options="{
        paint: {
          'circle-color': ['get', 'color'],
          'circle-radius': 8,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#fff',
          // 'circle-opacity': [
          //   'interpolate',
          //   ['linear'],
          //   ['get', 'ageInDays'],
          //   0, 1.0,     // Very recent (today): 100% opacity
          //   1, 0.9,     // 1 day old: 90% opacity
          //   7, 0.8,     // 1 week old: 80% opacity
          //   30, 0.7,    // 1 month old: 70% opacity
          //   90, 0.5,    // 3 months old: 50% opacity
          //   365, 0.3    // 1 year old: 30% opacity
          // ]
        },
        filter: ['all', ['!has', 'point_count'], ['!has', 'thumbUrl']]
      }"
      @click="(e) => handleClick(e.features[0], e)"
    ></MapLayer>

    <!-- Image points -->
    <MapLayer
      :map="map"
      :id="'project-' + project.project_id + '-reports_points_with_resources-layer'"
      :source="'project-' + project.project_id + '-features-points'"
      type="symbol"
      :options="{
        layout: {
          'icon-image': ['get', 'thumbUrl'],
          'icon-size': 0.1,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-anchor': 'center'
        },
        paint: {
          // 'icon-opacity': [
          //   'interpolate',
          //   ['linear'],
          //   ['get', 'ageInDays'],
          //   0, 1.0,     // Very recent (today): 100% opacity
          //   1, 0.9,     // 1 day old: 90% opacity
          //   7, 0.8,     // 1 week old: 80% opacity
          //   30, 0.7,    // 1 month old: 70% opacity
          //   90, 0.5,    // 3 months old: 50% opacity
          //   365, 0.3    // 1 year old: 30% opacity
          // ]
        },
        filter: ['all', ['!has', 'point_count'], ['has', 'thumbUrl']]
      }"
      @click="(e) => handleClick(e.features[0], e)"
    ></MapLayer>
  </MapSource>
</div>

<!-- Project Boundaries (for non-current projects) -->
<div v-if="feature && !isCurrentProject && activeTab === 'projects' && showProjectBoundaries">
  <MapSource
    :map="map"
    type="geojson"
    :id="'project-' + project.project_id"
    :data="feature"
  >
    <MapLayer
      :map="map"
      :id="'project-' + project.project_id + '-fill'"
      :source="'project-' + project.project_id"
      type="fill"
      :options="{
        paint: {
          'fill-color': '#0039A3',
          'fill-opacity': 0.2
        },
        filter: ['==', '$type', 'Polygon']
      }"
      @click="(e) => handleClick(e.features[0], e)"
    />
    <MapLayer
      :map="map"
      :id="'project-' + project.project_id + '-lines'"
      :source="'project-' + project.project_id"
      type="line"
      :options="{
        paint: {
          'line-color': '#0039A3',
          'line-width': 2,
          'line-dasharray': [4, 2]
        },
        filter: ['any', ['==', '$type', 'Polygon']]
      }"
      @click="(e) => handleClick(e.features[0], e)"
    />

    <MapSource
      v-if="featureCenter"
      :map="map"
      type="geojson"
      :id="'project-' + project.project_id + '-center'"
      :data="featureCenter"
    >
      <MapLayer
        :map="map"
        :id="'project-' + project.project_id + '-center'"
        :source="'project-' + project.project_id + '-center'"
        type="symbol"
        :options="{
          minzoom: 0,
          maxzoom: 24,
          layout: {
            'text-field': project.project_name,
            'text-anchor': 'center',
            'text-justify': 'center',
            'text-variable-anchor': ['center'],
            'text-radial-offset': 0,
            'text-size': 16,
            'text-font': ['Open Sans Bold'],
            'text-allow-overlap': true
          },
          'paint': {
            'text-color': 'white',
            'text-halo-color': '#0039A3',
            'text-halo-width': 1
          }
        }"
        @click="(e) => handleClick(e.features[0], e)"
      />
    </MapSource>
  </MapSource>
</div>

<!-- Feature Popup -->
<MapFeaturePopup
  v-if="showPopup && popupFeature && popupCoordinates"
  :map="map"
  :feature="popupFeature"
  :coordinates="popupCoordinates"
  :project-id="project.project_id"
  :org-id="project.organization_id"
  @close="closePopup"
/>
</template>
